import React, { useState } from 'react';
import { Box, Chip, Menu, MenuItem, styled, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { DropdownChipOption, DropdownChipProps } from './types';

export const DropdownChip: React.FC<DropdownChipProps> = ({
  options,
  handleSelect,
  disabled = false,
  placeholderOption,
  defaultColors = {},
  selected,
  dataTestId,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSelect = (option: DropdownChipOption) => {
    handleSelect(option);
    handleClose();
  };

  const getColor = (
    property: 'background' | 'text' | 'border',
    disabledColor: string,
    fallbackValue: string | undefined
  ) => {
    const selectedColor = selected?.colors[property];
    const placeholderColor = placeholderOption?.colors[property];
    const defaultColor = defaultColors[property];
    const color = selectedColor || (!selected && placeholderColor) || defaultColor || fallbackValue;
    return disabled ? disabledColor : color;
  };

  const disabledBackgroundColor = theme.palette.white.medium;
  const backgroundColor = getColor('background', disabledBackgroundColor, theme.palette.white.main);
  const textColor = getColor('text', theme.palette.text.tertiary, theme.palette.black.main);
  const borderColor = getColor('border', theme.palette.gray.darker, undefined);

  const label = (
    <ChipLabelContainer>
      <Typography
        sx={{
          fontWeight: theme.typography.fontWeightMedium,
          lineHeight: theme.spacing(3),
        }}
      >
        {selected ? selected.label : placeholderOption?.label}
      </Typography>
      {!disabled && <ExpandMore sx={{ height: theme.spacing(3), width: theme.spacing(3) }} />}
    </ChipLabelContainer>
  );

  return (
    <>
      <Chip
        label={label}
        onClick={handleClick}
        disabled={disabled}
        sx={{
          backgroundColor: backgroundColor,
          color: textColor,
          cursor: disabled ? 'default' : 'pointer',
          width: '133px',
          border: borderColor ? `2px solid ${borderColor}` : 'none',
          '&.Mui-disabled': {
            opacity: 1,
          },
        }}
        data-testid={dataTestId}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid={`${dataTestId}-menu`}
      >
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => onSelect(option)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ChipLabelContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});
